import React, { FC, useEffect, useState }  from "react";
import { useLocation, useHistory }         from "react-router-dom";
import _                                   from "underscore";
import { useCentralCalls }                 from "@src/rest/CentralCalls";
import { useCurrentUser, useSystemModals } from "@src/hooks";
import { DASH_VIEWS, viewConfigs }         from "@src/constants/DashEnums";
import DashSidebar                         from "@src/views/pages/projects/dash/DashSidebar";
import { Page, PageTitleContainer }        from "@src/views/containers";
import DashGrid, { DashGridItem } from "./DashGrid";
import { FlexAlign }                       from "@src/types";
import "./Dash.scss";
import DashOperationsDialog                from "@src/views/pages/projects/dash/DashOperationsDialog";

export interface DashPage {
    id: string;
    name: string;
    description: string;
    pageConfig: DashGridItem[];
    icon: string;
}

const initialDashPage: DashPage = {
    id: "",
    name: "",
    description: "",
    pageConfig: [],
    icon: "",
};

const DashContainerComponent: FC = () => {
    const [dashPages, setDashPages] = useState<DashPage[]>([]);
    const [activeView, setActiveView] = useState<DashPage>(initialDashPage);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [newPageConfirm, setNewPageConfirm] = useState(false);
    const [deletePageConfirm, setDeletePageConfirm] = useState(false);
    const [refreshKey, setRefreshKey] = useState<number>(1);
    const [newPageTitle, setNewPageTitle] = useState("");
    const [newPageDescription, setNewPageDescription] = useState("");
    const [newPageIcon, setNewPageIcon] = useState("");
    const modalActions = useSystemModals();
    const { TableSelect, RunUpdate, RunInsert } = useCentralCalls();
    const { currentUser } = useCurrentUser();
    const location = useLocation();
    const history = useHistory();
    const [sideBarCollapsed, setSideBarCollapsed] = useState(false);

    // Load initial dash pages
    useEffect(() => {
        let mounted = true;

        const loadInitialData = async () => {
            if (!mounted) return;
            try {
                setLoading(true);
                const data = await TableSelect("DashPage", {
                    userId: currentUser.id,
                    Hidden: false,
                });

                if (!mounted) return;

                if (data && Array.isArray(data)) {
                    setDashPages(data);

                    // Preserve the current active page on refresh; only fall back
                    // to the first page on the initial load (when activeView has no id).
                    setActiveView(prev => {
                        if (prev.id) {
                            const stillExists = data.find(p => p.id === prev.id);
                            return stillExists ?? (data[0] ?? initialDashPage);
                        }
                        return data[0] ?? initialDashPage;
                    });
                }
            } catch (error) {
                console.error("Error loading initial data:", error);
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadInitialData();
        return () => {
            mounted = false;
        };
    }, [currentUser.id, refreshKey]);

    const parsePageConfig = (config: any): DashGridItem[] => {
        if (Array.isArray(config)) return config; // ✅ Already correct, return as-is

        if (typeof config === "string") {
            try {
                const parsed = JSON.parse(config);
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                console.error("Error parsing pageConfig:", error);
                return [];
            }
        }

        return [];
    };

    const getViewContent = () => {
        if (loading) {
            return <div key="loading-state">Loading dashboard content...</div>;
        }

        if (activeView) {
            return (
                <DashGrid
                    key={`dash-grid-${activeView.id}`}
                    pageJson={parsePageConfig(activeView.pageConfig)}
                    editMode={editMode}
                    dashPageId={activeView.id}
                    onExitEditMode={toggleEditMode}
                />
            );
        }

        return <div key="no-view">No active view selected</div>;
    };

    const handleDashPageChange = (id: string) => {
        const selectedPage = dashPages.find((page) => page.id === id);
        if (selectedPage) {
            setActiveView(selectedPage);
        }
    };

    const toggleSidebar = () => {
        setSideBarCollapsed((prev) => !prev);
    };

    const toggleEditMode = () => {
        setEditMode(prev => !prev);
    };

    const handleDeletePage = async () => {
        if (activeView) {
            await RunUpdate("DashPage", activeView.id, { Hidden: true });
            setDeletePageConfirm(false);
            setRefreshKey(refreshKey+1);
        }
    };

    // Define a new function that can be passed to the sidebar
    const toggleDeletePage = async (pageId: string) => {
        try {
            // Set the Hidden flag to true to soft-delete the page
            await RunUpdate("DashPage", pageId, { Hidden: true });

            // Update the local state
            setDashPages(prevPages => prevPages.filter(page => page.id !== pageId));

            // If the deleted page was the active view, select another page
            if (activeView && activeView.id === pageId) {
                const remainingPages = dashPages.filter(page => page.id !== pageId);
                if (remainingPages.length > 0) {
                    setActiveView(remainingPages[0]);
                } else {
                    setActiveView(initialDashPage);
                }
            }

            // Trigger a refresh
            setRefreshKey(prev => prev + 1);

            return true;
        } catch (error) {
            console.error('Error deleting page:', error);
            return false;
        }
    };

    // Define a parameterless function for the onNewPage prop
    const handleNewPage = () => {
        setNewPageConfirm(true);
    };

    const handleNewPageSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPageTitle.trim()) {
            alert("Title is required!");
            return;
        }

        await RunInsert("DashPage", {
            Name: newPageTitle,
            Description: newPageDescription,
            PageConfig: "[]",
            Icon: newPageIcon,
        });
        setRefreshKey(refreshKey+1);
        setNewPageDescription("");
        setNewPageIcon("");
        setNewPageTitle("");
        setNewPageConfirm(false);
    };

    return (
        <Page name="project-dashboard" padMainHorizontal={false} padMainVertical={false} allowContentScroll className="dashContainerPage">
            <PageTitleContainer
                title={activeView?.name}
                description={activeView?.description}
                fluid
                alignContent={FlexAlign.CENTER}
                hideOnMinimization
                rightFlexItemProps={{
                    className: "text-right",
                    children: <DashOperationsDialog key="operations-dialog" editMode={editMode} onToggleEdit={toggleEditMode} onNewPage={handleNewPage} onDeletePage={() => setDeletePageConfirm(true)} dashPages={dashPages} onDashPageChange={handleDashPageChange} />,
                }}
            />
            <div className={`dashboard-grid ${sideBarCollapsed ? "sidebar-collapsed" : ""}`}>
                <div className={`dashboard-sidebar ${sideBarCollapsed ? "collapsed" : ""}`}>
                    <DashSidebar
                        key={`dash-sidebar-${refreshKey}`}
                        activeView={activeView?.id}
                        setActiveView={setActiveView}
                        isCollapsed={sideBarCollapsed}
                        toggleSideBar={toggleSidebar}
                        editMode={editMode}
                        toggleEditMode={toggleEditMode}
                        toggleDeletePage={toggleDeletePage}
                        onNewPage={handleNewPage}
                    />
                </div>

                <div className="dashboard-content">
                    {getViewContent()}
                </div>
            </div>
        </Page>
    );
};

export default DashContainerComponent;
